<script setup lang="ts">
  import { onMounted, ref } from 'vue';

  defineOptions({
    name: 'll-expand',
  });

  export type ExpandOuterElement = 'div' | 'td' | 'li';

  export interface ExpandProps {
    /**
     * The root element to use as the root element the expand transition gets applied to
     */
    is?: ExpandOuterElement;

    /**
     * Dictates whether the content is expanded or collapsed
     */
    isExpanded?: boolean;

    /**
     * Whether to only render content on expand
     */
    isLazy?: boolean;

    /**
     * The name of the transition to pass to the transition component
     * @see: https://vuejs.org/guide/built-ins/transition.html#css-based-transitions
     */
    transitionName?: string;
  }

  const props = withDefaults(defineProps<ExpandProps>(), {
    is: 'div',
    isExpanded: false,
    isLazy: false,
    transitionName: 'expand',
  });

  const emit = defineEmits<{
    (e: 'after-expand'): void;
  }>();

  const contentRef = ref<HTMLElement | null>(null);

  onMounted(() => {
    if (!props.isExpanded && contentRef.value) {
      contentRef.value.style.overflow = 'hidden';
    }
  });

  /**
   * Expands the element
   * @param {object} element - the element being expanded
   * @returns {void}
   */
  const onExpand = (element) => {
    // Trick to calculate the height of the hidden element
    element.style.visibility = 'hidden';
    element.style.height = 'auto';

    const height = element.offsetHeight;

    // Restore default styling
    element.style.visibility = '';
    element.style.display = '';

    // Set height on the element to zero
    element.style.height = 0;

    // Set expanded height after the other height change takes affect
    requestAnimationFrame(() => {
      element.style.height = `${height}px`;
    });
  };

  /**
   * Resores default styling after the expand animation is completed
   * @param {object} element - the element being expanded
   * @returns {void}
   */
  const afterExpand = (element) => {
    element.style.height = '';
    element.style.overflow = '';
    emit('after-expand');
  };

  /**
   * Collapses the element
   * @param {object} element - the element being expanded
   * @returns {void}
   */
  const onCollapse = (element) => {
    const height = element.offsetHeight;

    // Set height to the expanded height
    element.style.height = `${height}px`;
    element.style.overflow = 'hidden';

    // Set height to zero after the other height change takes affect
    requestAnimationFrame(() => {
      element.style.height = 0;
    });
  };
</script>

<template>
  <component
    :is="props.is"
    class="stash-expand"
    data-test="stash-expand"
    :class="{
      'stash-expand--expanded': isExpanded,
      'stash-expand--collapsed': !isExpanded,
    }"
  >
    <transition :name="transitionName" @after-enter="afterExpand" @enter="onExpand" @leave="onCollapse">
      <template v-if="isLazy">
        <div v-if="isExpanded" ref="contentRef">
          <slot></slot>
        </div>
      </template>
      <template v-else>
        <div v-show="isExpanded" ref="contentRef">
          <slot></slot>
        </div>
      </template>
    </transition>
  </component>
</template>
